class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    // Initialize intervals
    this.interval = null;
    this.intervalUntilMidnight = null;
    this.now = null;

    this.splitDigits = this.hasAttribute("data-split-digits");

    // Initialize countdown ui elements
    this.daysSpan = this.querySelector("[data-days]");
    this.hoursSpan = this.querySelector("[data-hours]");
    this.minutesSpan = this.querySelector("[data-minutes]");
    this.secondsSpan = this.querySelector("[data-seconds]");
  }

  get targetDate() {
    // Get the target date and time from the element attributes.
    const date = this.getAttribute("data-date");
    const time = this.getAttribute("data-format") || "00:00";
    // const gmt = `GMT${this.getAttribute('data-store-timezone')}`
    const gmtOffset = this.getAttribute("data-store-timezone") || "+00:00";
    
    // Convert the time to ISO 8601 format (safari fix)
    const isoDate = `${date}T${time}${gmtOffset}`;
    return new Date(isoDate);
  }

  get storeTime() {
    // Get the store date and time from the data-store-time attributes.
    const storeTimeStr = this.getAttribute("data-store-time");
    return new Date(storeTimeStr);
  }

  flipDigit(box, newVal) {
    const prev = box.dataset.currentDigit;
    if (prev === newVal) return;

    box.dataset.currentDigit = newVal;

    const top = box.querySelector(".cc-cd__card-top");
    const bottom = box.querySelector(".cc-cd__card-bottom");
    const flapFront = box.querySelector(".cc-cd__card-flap-front");
    const flapBack = box.querySelector(".cc-cd__card-flap-back");

    if (!top || !bottom || !flapFront || !flapBack) return;

    if (prev === undefined) {
      top.textContent = newVal;
      bottom.textContent = newVal;
      flapFront.textContent = newVal;
      flapBack.textContent = newVal;
      return;
    }

    if (box.classList.contains("flipping")) {
      box.classList.remove("flipping");
      bottom.textContent = top.textContent;
      void box.offsetHeight;
    }

    flapFront.textContent = prev;
    flapBack.textContent = newVal;
    top.textContent = newVal;

    box.classList.add("flipping");

    flapBack.addEventListener(
      "animationend",
      () => {
        bottom.textContent = newVal;
        box.classList.remove("flipping");
      },
      { once: true }
    );
  }

  displayCountdownValues(days, hours, minutes, seconds) {
    if (this.splitDigits) {
      const pad = (n) => String(n).padStart(2, "0");
      [
        [this.daysSpan, days],
        [this.hoursSpan, hours],
        [this.minutesSpan, minutes],
        [this.secondsSpan, seconds],
      ].forEach(([el, val]) => {
        if (!el) return;
        const digits = pad(val);
        const d0 = el.querySelector("[data-digit-0]");
        const d1 = el.querySelector("[data-digit-1]");
      
        if (d0) this.flipDigit(d0, digits[0]);
        if (d1) this.flipDigit(d1, digits[1]);
      });
      return;
    }
    
    this.daysSpan.textContent = days;
    if (this.daysSpan.nextElementSibling) {
      this.daysSpan.nextElementSibling.textContent =
        days === 1 ? "day" : "days";
    }
    this.hoursSpan.textContent = hours;
    if (this.hoursSpan.nextElementSibling) {
      this.hoursSpan.nextElementSibling.textContent =
        hours === 1 ? "hour" : "hours";
    }
    this.minutesSpan.textContent = minutes;
    if (this.minutesSpan.nextElementSibling) {
      this.minutesSpan.nextElementSibling.textContent =
        minutes === 1 ? "minute" : "minutes";
    }
    this.secondsSpan.textContent = seconds;
  }

  calculateTimeDifference(timestamp) {
    // calculate days, hours, minutes, and seconds.
    const days = Math.floor(timestamp / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timestamp % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timestamp % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timestamp % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }

  clearAllIntervals() {
    // clear countdown interval until target date
    clearInterval(this.interval);

    // clear countdown interval until midnight
    clearInterval(this.intervalUntilMidnight);

    // clear interval for updating store time now
    clearInterval(this.now);
  }

  displayCountdownTimer(time) {
    if (time <= 0) {
      // reset countdown
      this.displayCountdownValues(0, 0, 0, 0);

      this.clearAllIntervals();
    } else {
      // Get the calculated time difference
      const { days, hours, minutes, seconds } =
        this.calculateTimeDifference(time);

      // Display the result in the countdown ui elements.
      this.displayCountdownValues(days, hours, minutes, seconds);
    }
  }

  countdownUntilMidnight() {
    // Get the current date and time
    const currentDate = new Date();

    // Calculate the time until midnight
    const midnight = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + 1, // Next day
      0, // Midnight hour
      0, // Midnight minute
      0 // Midnight second
    );

    // Calculate the difference between now and midnight
    const difference = midnight - currentDate;

    // Display the countdown timer
    this.displayCountdownTimer(difference);
  }

  updateCountdown() {
    // Find the difference between now and the target date and time.
    const endDate = new Date(this.targetDate);
    const difference = endDate - this.now;

    // Display the countdown timer
    this.displayCountdownTimer(difference);
  }

  connectedCallback() {
    // store the current store time
    this.now = this.storeTime;

    // Function to update 'now' every second
    const updateNow = () => {
      this.now = new Date();
    };

    // Call the 'updateNow' function initially
    updateNow();

    // Update 'now' every second
    this.now = setInterval(updateNow, 1000);

    if (!isNaN(new Date(this.targetDate).getTime())) {
      // check if target date is valid and update countdown every second
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    } else {
      // update countdown every second until midnight (if target date is invalid)
      this.intervalUntilMidnight = setInterval(
        () => this.countdownUntilMidnight(),
        1000
      );
    }
  }

  disconnectedCallback() {
    this.clearAllIntervals();
  }
}

// Define the custom element.
customElements.define("countdown-timer", CountdownTimer);
